import { ROUTES } from '@/constants/routes'
import type { TaskReminder } from '@/modules/tasks/types/task.types'

export const TASK_REMINDER_KINDS = ['overdue', 'todo'] as const
export type TaskReminderKind = (typeof TASK_REMINDER_KINDS)[number]

export type TaskReminderSlide = {
  kind: TaskReminderKind
  label: string
  count: number
}

/** Shared desktop + mobile reminder timing (desktop chip defaults). */
/** Short pause after app/site open before the first burst. */
export const REMINDER_FIRST_DELAY_MS = 1000
/** Mobile FAB — wait longer so the banner doesn’t interrupt first paint / navigation. */
export const REMINDER_MOBILE_FIRST_DELAY_MS = 5 * 60 * 1000
export const REMINDER_STATUS_HOLD_MS = 4000
/** Quiet period after a burst finishes (only when not once-per-session). */
export const REMINDER_CYCLE_GAP_MS = 2 * 60 * 1000
/** How many full status-list passes per visible burst (desktop chip). */
export const REMINDER_LOOPS_PER_BURST = 2
/** Mobile FAB — a single pass through overdue → to-do (no double loop). */
export const REMINDER_MOBILE_LOOPS_PER_BURST = 1

/** sessionStorage — survives refresh in the same tab; cleared when the tab/app session ends. */
export const REMINDER_SESSION_STORAGE_KEY = 'pmms.task-reminder.session'

export type TaskReminderSessionState = {
  /** When the open-app wait clock started. */
  startedAt: number
  /** First attention burst already finished this browser session. */
  burstCompleted: boolean
}

export type TaskReminderCycleOptions = {
  firstDelayMs?: number
  loopsPerBurst?: number
  cycleGapMs?: number
  statusHoldMs?: number
  /**
   * One attention burst per browser tab session (survives refresh / module changes).
   * After it finishes, do not auto-show again until a new browser session.
   */
  oncePerSession?: boolean
}

export function readReminderSession(): TaskReminderSessionState | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(REMINDER_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<TaskReminderSessionState>
    if (typeof parsed.startedAt !== 'number' || typeof parsed.burstCompleted !== 'boolean') {
      return null
    }
    return { startedAt: parsed.startedAt, burstCompleted: parsed.burstCompleted }
  } catch {
    return null
  }
}

export function writeReminderSession(state: TaskReminderSessionState): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(REMINDER_SESSION_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Private mode / quota — cycle still works in-memory for this mount.
  }
}

const SLIDE_META: Record<TaskReminderKind, { label: string; countKey: keyof TaskReminder }> = {
  overdue: { label: 'Overdue', countKey: 'overdueCount' },
  todo: { label: 'To do', countKey: 'todoCount' },
}

/** Skip empty buckets — Overdue → To do (R2). */
export function taskReminderSlides(data: TaskReminder | undefined): TaskReminderSlide[] {
  if (!data) return []
  return TASK_REMINDER_KINDS.flatMap((kind) => {
    const count = data[SLIDE_META[kind].countKey]
    if (!count) return []
    return [{ kind, label: SLIDE_META[kind].label, count }]
  })
}

export function parseTaskReminderParam(value: string | null): TaskReminderKind | null {
  if (value === 'overdue' || value === 'todo') return value
  return null
}

export function tasksReminderHref(kind: TaskReminderKind): string {
  return `${ROUTES.TASKS}?reminder=${kind}`
}

export function formatReminderCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}
